import axios from "axios";
export var ContentType;
(function (ContentType) {
    ContentType["Json"] = "application/json";
    ContentType["FormData"] = "multipart/form-data";
    ContentType["UrlEncoded"] = "application/x-www-form-urlencoded";
    ContentType["Text"] = "text/plain";
})(ContentType || (ContentType = {}));
export class HttpClient {
    instance;
    securityData = null;
    securityWorker;
    secure;
    format;
    constructor({ securityWorker, secure, format, ...axiosConfig } = {}) {
        this.instance = axios.create({ ...axiosConfig, baseURL: axiosConfig.baseURL || "/sleepwellMTS" });
        this.secure = secure;
        this.format = format;
        this.securityWorker = securityWorker;
    }
    setSecurityData = (data) => {
        this.securityData = data;
    };
    mergeRequestParams(params1, params2) {
        const method = params1.method || (params2 && params2.method);
        return {
            ...this.instance.defaults,
            ...params1,
            ...(params2 || {}),
            headers: {
                ...((method && this.instance.defaults.headers[method.toLowerCase()]) || {}),
                ...(params1.headers || {}),
                ...((params2 && params2.headers) || {}),
            },
        };
    }
    stringifyFormItem(formItem) {
        if (typeof formItem === "object" && formItem !== null) {
            return JSON.stringify(formItem);
        }
        else {
            return `${formItem}`;
        }
    }
    createFormData(input) {
        return Object.keys(input || {}).reduce((formData, key) => {
            const property = input[key];
            const propertyContent = property instanceof Array ? property : [property];
            for (const formItem of propertyContent) {
                const isFileType = formItem instanceof Blob || formItem instanceof File;
                formData.append(key, isFileType ? formItem : this.stringifyFormItem(formItem));
            }
            return formData;
        }, new FormData());
    }
    request = async ({ secure, path, type, query, format, body, ...params }) => {
        const secureParams = ((typeof secure === "boolean" ? secure : this.secure) &&
            this.securityWorker &&
            (await this.securityWorker(this.securityData))) ||
            {};
        const requestParams = this.mergeRequestParams(params, secureParams);
        const responseFormat = format || this.format || undefined;
        if (type === ContentType.FormData && body && body !== null && typeof body === "object") {
            body = this.createFormData(body);
        }
        if (type === ContentType.Text && body && body !== null && typeof body !== "string") {
            body = JSON.stringify(body);
        }
        return this.instance.request({
            ...requestParams,
            headers: {
                ...(requestParams.headers || {}),
                ...(type && type !== ContentType.FormData ? { "Content-Type": type } : {}),
            },
            params: query,
            responseType: responseFormat,
            data: body,
            url: path,
        });
    };
}
/**
 * @title External Data Exchange documentation
 * @version v2.0.2624.261
 * @baseUrl /sleepwellMTS
 */
export class Api extends HttpClient {
    argos = {
        /**
         * No description
         *
         * @tags Argos
         * @name DownloadArgosControllersDataList
         * @summary Download data from Argos central controller to Argos database
         * @request GET:/Argos/DownloadArgosControllersData
         */
        downloadArgosControllersDataList: (params = {}) => this.request({
            path: `/Argos/DownloadArgosControllersData`,
            method: "GET",
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags Argos
         * @name SetClockList
         * @summary Set controller clocks
         * @request GET:/Argos/SetClock
         */
        setClockList: (query, params = {}) => this.request({
            path: `/Argos/SetClock`,
            method: "GET",
            query: query,
            format: "json",
            ...params,
        }),
    };
    axapta = {
        /**
         * No description
         *
         * @tags Axapta
         * @name DownloadAbsencesList
         * @summary Download absences data from Axapta
         * @request GET:/Axapta/DownloadAbsences
         */
        downloadAbsencesList: (query, params = {}) => this.request({
            path: `/Axapta/DownloadAbsences`,
            method: "GET",
            query: query,
            format: "json",
            ...params,
        }),
    };
    columbus = {
        /**
         * No description
         *
         * @tags Columbus
         * @name ImportEmployeeDataCreate
         * @summary Query employee data from Columbus and export these to WTS
         * @request POST:/Columbus/ImportEmployeeData
         */
        importEmployeeDataCreate: (data, params = {}) => this.request({
            path: `/Columbus/ImportEmployeeData`,
            method: "POST",
            body: data,
            type: ContentType.Json,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags Columbus
         * @name ImportPositionDataList
         * @summary Query position data from Columbus
         * @request GET:/Columbus/ImportPositionData
         */
        importPositionDataList: (params = {}) => this.request({
            path: `/Columbus/ImportPositionData`,
            method: "GET",
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags Columbus
         * @name ImportJobDataList
         * @summary Query job data from Columbus
         * @request GET:/Columbus/ImportJobData
         */
        importJobDataList: (params = {}) => this.request({
            path: `/Columbus/ImportJobData`,
            method: "GET",
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags Columbus
         * @name ImportDepartmentDataList
         * @summary Query department data from Columbus and export these to WTS
         * @request GET:/Columbus/ImportDepartmentData
         */
        importDepartmentDataList: (query, params = {}) => this.request({
            path: `/Columbus/ImportDepartmentData`,
            method: "GET",
            query: query,
            format: "json",
            ...params,
        }),
    };
    configurations = {
        /**
         * No description
         *
         * @tags Configurations
         * @name ConfigurationsCreate
         * @summary Set new configuration
         * @request POST:/Configurations
         */
        configurationsCreate: (data, params = {}) => this.request({
            path: `/Configurations`,
            method: "POST",
            body: data,
            type: ContentType.Json,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags Configurations
         * @name ConfigurationsUpdate
         * @summary Update configuration
         * @request PUT:/Configurations
         */
        configurationsUpdate: (data, params = {}) => this.request({
            path: `/Configurations`,
            method: "PUT",
            body: data,
            type: ContentType.Json,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags Configurations
         * @name ConfigurationsList
         * @summary Gets configurations
         * @request GET:/Configurations
         */
        configurationsList: (query, params = {}) => this.request({
            path: `/Configurations`,
            method: "GET",
            query: query,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags Configurations
         * @name ConfigurationsDelete
         * @summary Deletes configurations
         * @request DELETE:/Configurations
         */
        configurationsDelete: (query, params = {}) => this.request({
            path: `/Configurations`,
            method: "DELETE",
            query: query,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags Configurations
         * @name ClientSecretUpdate
         * @summary Updates client secret
         * @request PUT:/Configurations/ClientSecret
         */
        clientSecretUpdate: (data, params = {}) => this.request({
            path: `/Configurations/ClientSecret`,
            method: "PUT",
            body: data,
            type: ContentType.Json,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags Configurations
         * @name StartUpdate
         * @summary Start task with given configuration id
         * @request PUT:/Configurations/Start
         */
        startUpdate: (data, params = {}) => this.request({
            path: `/Configurations/Start`,
            method: "PUT",
            body: data,
            type: ContentType.Json,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags Configurations
         * @name StopUpdate
         * @summary Stop task with given configuration id
         * @request PUT:/Configurations/Stop
         */
        stopUpdate: (data, params = {}) => this.request({
            path: `/Configurations/Stop`,
            method: "PUT",
            body: data,
            type: ContentType.Json,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags Configurations
         * @name RunningList
         * @summary Get running Argos configurations
         * @request GET:/Configurations/Running
         */
        runningList: (query, params = {}) => this.request({
            path: `/Configurations/Running`,
            method: "GET",
            query: query,
            format: "json",
            ...params,
        }),
    };
    configurationTypes = {
        /**
         * No description
         *
         * @tags ConfigurationTypes
         * @name ConfigurationTypesList
         * @summary Gets configuration types
         * @request GET:/ConfigurationTypes
         */
        configurationTypesList: (params = {}) => this.request({
            path: `/ConfigurationTypes`,
            method: "GET",
            format: "json",
            ...params,
        }),
    };
    eduId = {
        /**
         * No description
         *
         * @tags EduId
         * @name ImportCardTypesCreate
         * @summary Query card types from EduId and export these to WTS
         * @request POST:/EduId/ImportCardTypes
         */
        importCardTypesCreate: (data, params = {}) => this.request({
            path: `/EduId/ImportCardTypes`,
            method: "POST",
            body: data,
            type: ContentType.Json,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags EduId
         * @name ImportSchoolCardsCreate
         * @summary Query school cards from EduId and export these to WTS
         * @request POST:/EduId/ImportSchoolCards
         */
        importSchoolCardsCreate: (data, params = {}) => this.request({
            path: `/EduId/ImportSchoolCards`,
            method: "POST",
            body: data,
            type: ContentType.Json,
            format: "json",
            ...params,
        }),
    };
    generator = {
        /**
         * No description
         *
         * @tags Generator
         * @name GenerateList
         * @summary Generate workhours for company
         * @request GET:/Generator/Generate
         */
        generateList: (query, params = {}) => this.request({
            path: `/Generator/Generate`,
            method: "GET",
            query: query,
            format: "json",
            ...params,
        }),
    };
    merit = {
        /**
     * No description
     *
     * @tags Merit
     * @name UsersCreate
     * @summary Query users from Merit and write new/changed data to database
    db data is taken from first enabled Merit configuration
     * @request POST:/Merit/Users
     */
        usersCreate: (data, params = {}) => this.request({
            path: `/Merit/Users`,
            method: "POST",
            body: data,
            type: ContentType.Json,
            format: "json",
            ...params,
        }),
        /**
     * No description
     *
     * @tags Merit
     * @name SendToMeritCreate
     * @summary Query workhours and/or absence data from API (main report cache ID used for data collection)
    and send collected data to Merit.
     * @request POST:/Merit/SendToMerit
     */
        sendToMeritCreate: (data, params = {}) => this.request({
            path: `/Merit/SendToMerit`,
            method: "POST",
            body: data,
            type: ContentType.Json,
            format: "json",
            ...params,
        }),
    };
    persona = {
        /**
         * No description
         *
         * @tags Persona
         * @name ImportUsersList
         * @summary Import users and contracts starting from specific date and for specific department(s)
         * @request GET:/Persona/ImportUsers
         */
        importUsersList: (query, params = {}) => this.request({
            path: `/Persona/ImportUsers`,
            method: "GET",
            query: query,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags Persona
         * @name ImportLeavesList
         * @summary Import leaves starting from specific date and for specific department(s)
         * @request GET:/Persona/ImportLeaves
         */
        importLeavesList: (query, params = {}) => this.request({
            path: `/Persona/ImportLeaves`,
            method: "GET",
            query: query,
            format: "json",
            ...params,
        }),
    };
    pilvepalk = {
        /**
         * No description
         *
         * @tags Pilvepalk
         * @name ImportEmployeeDataCreate
         * @summary Query employee data from Pilvepalk and export these to WTS
         * @request POST:/Pilvepalk/ImportEmployeeData
         */
        importEmployeeDataCreate: (data, params = {}) => this.request({
            path: `/Pilvepalk/ImportEmployeeData`,
            method: "POST",
            body: data,
            type: ContentType.Json,
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags Pilvepalk
         * @name ImportPositionDataList
         * @summary Query position data from Pilvepalk
         * @request GET:/Pilvepalk/ImportPositionData
         */
        importPositionDataList: (params = {}) => this.request({
            path: `/Pilvepalk/ImportPositionData`,
            method: "GET",
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags Pilvepalk
         * @name ImportJobDataList
         * @summary Query job data from Pilvepalk
         * @request GET:/Pilvepalk/ImportJobData
         */
        importJobDataList: (params = {}) => this.request({
            path: `/Pilvepalk/ImportJobData`,
            method: "GET",
            format: "json",
            ...params,
        }),
        /**
         * No description
         *
         * @tags Pilvepalk
         * @name ImportDepartmentDataList
         * @summary Query department data from Pilvepalk and export these to WTS
         * @request GET:/Pilvepalk/ImportDepartmentData
         */
        importDepartmentDataList: (query, params = {}) => this.request({
            path: `/Pilvepalk/ImportDepartmentData`,
            method: "GET",
            query: query,
            format: "json",
            ...params,
        }),
    };
    requestHistory = {
        /**
         * No description
         *
         * @tags RequestHistory
         * @name RequestHistoryList
         * @summary Returns Request history objects
         * @request GET:/RequestHistory
         */
        requestHistoryList: (query, params = {}) => this.request({
            path: `/RequestHistory`,
            method: "GET",
            query: query,
            format: "json",
            ...params,
        }),
    };
    tasks = {
        /**
         * No description
         *
         * @tags Tasks
         * @name ImportTimesForPeriodCreate
         * @summary Imports machine times for given period
         * @request POST:/Tasks/ImportTimesForPeriod
         */
        importTimesForPeriodCreate: (query, params = {}) => this.request({
            path: `/Tasks/ImportTimesForPeriod`,
            method: "POST",
            query: query,
            ...params,
        }),
    };
    versions = {
        /**
         * No description
         *
         * @tags Versions
         * @name VersionsList
         * @summary Returns assembly and database versions
         * @request GET:/Versions
         */
        versionsList: (params = {}) => this.request({
            path: `/Versions`,
            method: "GET",
            format: "json",
            ...params,
        }),
    };
    yester = {
        /**
         * No description
         *
         * @tags Yester
         * @name ImportLeavesList
         * @summary Import leaves starting from changes from specific date
         * @request GET:/Yester/ImportLeaves
         */
        importLeavesList: (query, params = {}) => this.request({
            path: `/Yester/ImportLeaves`,
            method: "GET",
            query: query,
            format: "json",
            ...params,
        }),
    };
}
